// The forwarded card of a human turn in an agent transcript. The daemon's timeline names
// only the `messages` row (forwardedMessageId); the card itself always comes from the relay,
// which built it and re-checks this viewer's read access on every fetch.
import { SvelteMap } from "svelte/reactivity";
import type { ForwardedCard, Message, MessageCard } from "$lib/core/types.js";
import { reportUiFailure } from "$lib/report-failure.js";

interface MessageFetcher {
  fetchMessage(workspaceId: string, messageId: string): Promise<{ message: Message | null }>;
}

const forwardedOf = (card: MessageCard | null | undefined): ForwardedCard | undefined =>
  card?.kind === "forwarded" ? card : undefined;

export class ForwardedTurnCards {
  private readonly cards = new SvelteMap<string, ForwardedCard>();
  // Asked once per id: an answer with no card (deleted row, no access) is not asked again.
  private readonly asked = new Set<string>();

  /** A message row seen anywhere (a send's echo, a ledger page): remember its forwarded card. */
  note(message: { id: string; card?: MessageCard | null }): void {
    const card = forwardedOf(message.card);
    if (card) this.cards.set(message.id, card);
  }

  /** The relay re-rendered a card (the original was deleted): only a row we show is updated. */
  patch(messageId: string, card: MessageCard | null | undefined): void {
    if (!this.cards.has(messageId)) return;
    const next = forwardedOf(card);
    if (next) this.cards.set(messageId, next);
    else this.cards.delete(messageId);
  }

  get(messageId: string | undefined): ForwardedCard | undefined {
    return messageId ? this.cards.get(messageId) : undefined;
  }

  /** Fetch the card of a turn the timeline marked as a forward. Idempotent per id. */
  async ensure(
    fetcher: MessageFetcher,
    workspaceId: string | undefined,
    messageId: string,
  ): Promise<void> {
    if (!workspaceId || this.cards.has(messageId) || this.asked.has(messageId)) return;
    this.asked.add(messageId);
    try {
      const { message } = await fetcher.fetchMessage(workspaceId, messageId);
      if (message) this.note(message);
    } catch (err) {
      // A failed read is not an answer: the next mount asks again.
      this.asked.delete(messageId);
      reportUiFailure("agents.forwarded_turn_card", err, { workspaceId, messageId });
    }
  }

  reset(): void {
    this.cards.clear();
    this.asked.clear();
  }
}

export const forwardedTurnCards = new ForwardedTurnCards();
